import type { AddRecipeToShoppingListRequest, Recipe, RecipeListItem } from '@recipes/contracts';
import { formatIngredientLine } from '@recipes/domain';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  Stepper,
} from '@recipes/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecipeSearchList } from '@/features/shared/recipe-picker';
import { api } from '@/lib/api';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

/** Two-step dialog: pick a recipe, then servings + ingredient subset + pantry options. */
export function AddRecipeDialog({
  open,
  onOpenChange,
  onSubmit,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (body: AddRecipeToShoppingListRequest) => void;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<RecipeListItem | null>(null);
  const [servings, setServings] = useState(4);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [includeOptional, setIncludeOptional] = useState(false);
  const [subtractPantry, setSubtractPantry] = useState(false);

  useEffect(() => {
    if (!open) {
      setPicked(null);
      setSelected(new Set());
      setIncludeOptional(false);
      setSubtractPantry(false);
    }
  }, [open]);

  const recipeQuery = useQuery({
    queryKey: queryKeys.recipes.detail(picked?.id ?? ''),
    queryFn: () => api.recipes.get(picked?.id ?? ''),
    enabled: Boolean(picked),
  });
  const recipe = recipeQuery.data;

  useEffect(() => {
    if (!recipe) return;
    setServings(Math.max(1, Math.round(Number(recipe.servings)) || 1));
    setSelected(
      new Set(
        recipe.ingredientSections.flatMap((s) =>
          s.ingredients.filter((i) => !i.optional).map((i) => i.id),
        ),
      ),
    );
  }, [recipe]);

  useEffect(() => {
    if (!recipe) return;
    const optionalIds = recipe.ingredientSections.flatMap((s) =>
      s.ingredients.filter((i) => i.optional).map((i) => i.id),
    );
    setSelected((prev) => {
      const next = new Set(prev);
      optionalIds.forEach((id) => (includeOptional ? next.add(id) : next.delete(id)));
      return next;
    });
  }, [includeOptional, recipe]);

  const all = recipe ? recipe.ingredientSections.flatMap((s) => s.ingredients) : [];
  const allSelected = all.length > 0 && all.every((i) => selected.has(i.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} fullscreenOnMobile>
        <DialogHeader>
          <DialogTitle>{t('shopping.addRecipe.title')}</DialogTitle>
          <DialogDescription>
            {picked ? picked.title : t('shopping.addRecipe.pick')}
          </DialogDescription>
        </DialogHeader>
        {!picked && (
          <RecipeSearchList multiple={false} selected={[]} onToggle={setPicked} autoFocus />
        )}
        {picked && !recipe && <Spinner label={t('app.loading')} />}
        {picked && recipe && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">{t('common.servings')}</span>
              <Stepper
                value={servings}
                onChange={setServings}
                min={1}
                max={100}
                label={t('common.servings')}
                decreaseLabel={t('shopping.addRecipe.fewer')}
                increaseLabel={t('shopping.addRecipe.more')}
              />
              <span className="text-xs text-muted-foreground">
                {t('shopping.addRecipe.original', { servings: recipe.servings })}
              </span>
            </div>
            <IngredientChecklist
              recipe={recipe}
              selected={selected}
              onToggle={(id) =>
                setSelected((prev) => {
                  const n = new Set(prev);
                  if (n.has(id)) n.delete(id);
                  else n.add(id);
                  return n;
                })
              }
              onToggleAll={() =>
                setSelected(allSelected ? new Set() : new Set(all.map((i) => i.id)))
              }
              allSelected={allSelected}
            />
            <label className="flex min-h-[44px] items-center gap-3 text-sm">
              <Checkbox
                checked={includeOptional}
                onCheckedChange={(c) => setIncludeOptional(c === true)}
              />
              {t('shopping.addRecipe.includeOptional')}
            </label>
            <label className="flex min-h-[44px] items-center gap-3 text-sm">
              <Checkbox
                checked={subtractPantry}
                onCheckedChange={(c) => setSubtractPantry(c === true)}
              />
              {t('shopping.addRecipe.subtractPantry')}
            </label>
          </div>
        )}
        <DialogFooter>
          {picked && (
            <Button type="button" variant="ghost" onClick={() => setPicked(null)}>
              {t('common.back')}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!recipe || selected.size === 0}
            loading={saving}
            onClick={() => {
              if (!recipe) return;
              onSubmit({
                recipeId: recipe.id,
                servings: String(servings),
                ingredientIds: allSelected ? undefined : [...selected],
                includeOptional,
                subtractPantry,
              });
            }}
          >
            {t('shopping.addRecipe.submit', { count: selected.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IngredientChecklist({
  recipe,
  selected,
  onToggle,
  onToggleAll,
  allSelected,
}: {
  recipe: Recipe;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  allSelected: boolean;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  return (
    <fieldset className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
      <legend className="px-1 text-sm font-medium">{t('shopping.addRecipe.ingredients')}</legend>
      <label className="flex min-h-[40px] items-center gap-3 border-b pb-1 text-sm font-medium">
        <Checkbox checked={allSelected} onCheckedChange={onToggleAll} />
        {t('shopping.addRecipe.selectAll')}
      </label>
      {recipe.ingredientSections.map((section) => (
        <div key={section.id}>
          {section.title && (
            <p className="mt-1 text-xs font-semibold text-muted-foreground">{section.title}</p>
          )}
          {section.ingredients.map((ing) => (
            <label key={ing.id} className="flex min-h-[40px] items-center gap-3 text-sm">
              <Checkbox checked={selected.has(ing.id)} onCheckedChange={() => onToggle(ing.id)} />
              <span className={ing.optional ? 'text-muted-foreground' : undefined}>
                {formatIngredientLine({ ...ing, unitText: ing.unitText }, locale).line}
              </span>
            </label>
          ))}
        </div>
      ))}
    </fieldset>
  );
}
